import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase, saveChallenge } from '../supabaseClient';

const AIChallenges = ({ userId }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');
  const [difficulty, setDifficulty] = useState('medium');

  useEffect(() => {
    loadChallenges();
  }, [userId]);

  const loadChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setChallenges(data || []);
    } catch (error) {
      console.error('Error loading challenges:', error);
    }
  };

  const generateChallenge = async () => {
    if (!selectedGame) {
      alert('Please select a game first!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        'https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.2',
        {
          inputs: `[INST]You are a gaming challenge generator. Create a unique ${difficulty} difficulty challenge for ${selectedGame}. The challenge should be specific, measurable, and fun. Format your response as a single paragraph describing the challenge.[/INST]`
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_HUGGINGFACE_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const challengeText = response.data[0]?.generated_text.split('[/INST]').pop().trim();
      const challenge = {
        text: challengeText,
        game: selectedGame,
        difficulty: difficulty
      };

      await saveChallenge(userId, challenge);
      await loadChallenges();
    } catch (error) {
      console.error('Error generating challenge:', error);
      alert('Failed to generate challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const markChallengeComplete = async (challengeId) => {
    try {
      const { error } = await supabase
        .from('challenges')
        .update({
          completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', challengeId);

      if (error) throw error;
      await loadChallenges();
    } catch (error) {
      console.error('Error marking challenge complete:', error);
    }
  };

  return (
    <div className="space-y-6 p-4 bg-xkid-gray rounded-lg border border-xkid-green">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-xkid-green mb-4">AI Gaming Challenges</h2>
        <p className="text-gray-400">Generate unique challenges for your favorite games!</p>
      </div>

      <div className="space-y-4">
        <input
          type="text"
          value={selectedGame}
          onChange={(e) => setSelectedGame(e.target.value)}
          placeholder="Enter game name..."
          className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
        />

        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
          <option value="extreme">Extreme</option>
        </select>

        <button
          onClick={generateChallenge}
          disabled={loading}
          className="w-full p-3 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Challenge'}
        </button>
      </div>

      <div className="space-y-4 mt-8">
        <h3 className="text-xl font-bold text-xkid-green">Your Challenges</h3>
        {challenges.map((challenge) => (
          <div
            key={challenge.id}
            className={`p-4 rounded-lg border ${
              challenge.completed
                ? 'border-xkid-green bg-opacity-20 bg-xkid-green'
                : 'border-xkid-red bg-xkid-gray-light'
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-bold text-xkid-green">{challenge.game}</h4>
                <p className="text-gray-300 mt-2">{challenge.challenge_text}</p>
                <div className="flex items-center mt-2 space-x-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    challenge.difficulty === 'easy' ? 'bg-green-500' :
                    challenge.difficulty === 'medium' ? 'bg-yellow-500' :
                    challenge.difficulty === 'hard' ? 'bg-red-500' :
                    'bg-purple-500'
                  } text-black`}>
                    {challenge.difficulty}
                  </span>
                  <span className="text-gray-400 text-xs">
                    {new Date(challenge.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {!challenge.completed && (
                <button
                  onClick={() => markChallengeComplete(challenge.id)}
                  className="px-3 py-1 rounded bg-xkid-green text-black text-sm hover:bg-xkid-green-dark transition-colors"
                >
                  Complete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIChallenges; 